import React, { FC, useEffect } from 'react'
import { Flex, Link, Text } from '@moonpig/launchpad-components'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { createLinkHref } from '@moonpig/web-core-link'
import { useStoreId } from '@moonpig/web-core-stores'
import { useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { formatTitle } from '../Button'
import { useFindLocaleText } from '../../../text-localisation'
import { RemindersButtonProps } from './types'
import { getTitleForReminderMissionButton } from './getReminderMissionButtonTitle'
import {
  filterSupportedOccasions,
  getFirstName,
  getOccasionBackgroundImage,
  getReminderLinkUrl,
  getReminderMissionButtonFallbackLocaleKey,
  getReminderMissionButtonLocaleKey,
  SupportedOccasion,
} from './utils'
import { formatReminderDate } from './formatReminderDate'

const StyledReminderContent = styled(Flex)<{ $backgroundImage: string }>`
  border-radius: 100%;
  aspect-ratio: 1;
  background-image: url(${props => props.$backgroundImage});
  background-size: 80px;
  background-position: center;

  ${breakpointUp('md')} {
    background-size: 100px;
  }

  ${s({
    gap: 0,
    width: {
      xs: '80px',
      md: '100px',
    },
    border: 3,
    bgcolor: 'colorBackground05',
    borderColor: 'colorBlack00',
    boxShadow: { xs: 2, md: 0 },
    pt: { xs: 2, md: 3 },
  })}
`

const StyledReminderWrapper = styled.div`
  position: relative;
  line-height: 0;
  transform: translateY(0);
  transition: 200ms cubic-bezier(0.56, 0.1, 0.34, 0.91) transform;

  ${s({
    mb: 4,
  })}

  &::before {
    content: '';
    width: 100%;
    height: 100%;
    background: white;
    display: block;
    position: absolute;
    border-radius: 100%;
    inset: 0;
    opacity: 0;
    z-index: -1;
    transition: 200ms cubic-bezier(0.56, 0.1, 0.34, 0.91) opacity;

    ${s({
      boxShadow: { xs: 0, md: 2 },
      bgcolor: 'colorBlack00',
    })}
  }
`

const StyledDate = styled(Text)`
  text-shadow: 1px 1px 0px white;
  ${s({
    lineHeight: { xs: '1.5rem', md: '2.25rem' },
  })};
`

const StyledMonth = styled(Text)`
  text-shadow: 1px 1px 0px white;

  ${s({
    lineHeight: { xs: '1rem', md: '1.25rem' },
  })}
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  flex-direction: column;
  max-width: 145px;
  text-align: center;

  ${s({
    my: 3,
    pt: 5,
    pb: 2,
    pl: 4,
    pr: 0,
    borderRadius: 2,
    typography: 'typeBodyLabel',
    color: 'colorTextHeadline',
  })}

  &:hover {
    text-decoration: none;

    ${s({
      color: 'colorInteractionDefault',
    })}

    ${StyledReminderWrapper} {
      transform: translateY(-5px);

      &::before {
        opacity: 1;
      }
    }
  }
`

export const RemindersButton: FC<RemindersButtonProps> = ({
  onTrackClickEvent,
  reminders,
}) => {
  const storeId = useStoreId()
  const localiseText = useFindLocaleText()
  const supportedReminders = filterSupportedOccasions(reminders)
  const activeReminder =
    supportedReminders.length > 0 ? supportedReminders[0] : null
  const { trackGAEventOnce } = useTrackGAEventOnce()

  useEffect(() => {
    if (activeReminder) {
      const occasionLabel = activeReminder.occasion
        .toLowerCase()
        .replace(/_/g, ' ')
      trackGAEventOnce({
        event: 'impression',
        event_data: {
          category: 'home',
          action: `reminder mission button | ${occasionLabel}`,
          label: 'mission buttons',
          value: undefined,
          non_interaction: true,
        },
      })
    }
  }, [activeReminder, trackGAEventOnce])

  if (!activeReminder) return null

  const localeKey = getReminderMissionButtonLocaleKey(
    activeReminder.occasion as SupportedOccasion,
  )
  const reminderName = localiseText(localeKey, {
    name: getFirstName(activeReminder.name),
  })
  const fallbackLocaleKey = getReminderMissionButtonFallbackLocaleKey(
    activeReminder.occasion as SupportedOccasion,
  )
  const fallbackText = localiseText(fallbackLocaleKey)
  const titleToUse = getTitleForReminderMissionButton(
    reminderName,
    fallbackText,
  )

  const formattedDate = formatReminderDate(activeReminder.nextDate, storeId)
  const linkUrl = getReminderLinkUrl(activeReminder, storeId)
  const backgroundImage = getOccasionBackgroundImage(
    activeReminder.occasion as SupportedOccasion,
  )

  return (
    <StyledLink
      data-testid="reminders-mission-button"
      href={createLinkHref({
        url: linkUrl,
        trackEvent: onTrackClickEvent,
      })}
    >
      <StyledReminderWrapper>
        <StyledReminderContent
          $backgroundImage={backgroundImage}
          flexDirection="column"
          justifyContent="center"
        >
          {formattedDate?.day && (
            <StyledDate typography="typeDisplay01">
              {formattedDate.day}
            </StyledDate>
          )}
          {formattedDate?.month && (
            <StyledMonth typography="typeHeading1">
              {formattedDate.month}
            </StyledMonth>
          )}
        </StyledReminderContent>
      </StyledReminderWrapper>
      {formatTitle(titleToUse)}
    </StyledLink>
  )
}
